import { request } from '@core/api';
import { useAppDispatch } from '@core/redux/hooks';
import { toHumanReadable } from '@core/logic';
import { CircleInformationFilled, EyeOutline, EyeSlashedOutline } from '@deepup/icons';
import {
  Checkbox,
  Chip,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ProcessingIndicator } from '@processingResults/components';
import { scanThunks } from '@scans/redux';
import type { QAState, ReworkCategory, Scan } from '@scans/types';
import { qaStateDescriptions } from '@scans/types';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSnapshot } from 'valtio';
import { useScanQaColors } from '../../../hooks';
import { checkboxSelectionState, toggleCheckedScans } from '../../checkboxSelectionState';
import { categoryTitles } from '../../ReworkStatus';
import { scanGroupingState } from '../../scanGroupingState';

interface ScanRemarksResponse {
  remarks: string | null;
}

export const fetchComment = async (scanId: string) => {
  const response = await request<ScanRemarksResponse>('GET', {
    path: ['scans', scanId, 'remarks'],
  });
  return response.remarks;
};

const NO_COMMENT_TEXT = 'No comment available';
const LOADING_COMMENT_TEXT = 'Loading comment...';
const ERROR_COMMENT_TEXT = 'Error loading comment';

const QAStateIndicator = ({
  state,
  problems,
  scanId,
}: {
  state: QAState;
  problems: ReworkCategory[];
  scanId: string;
}) => {
  const { qaLabelColors } = useScanQaColors();
  const text = toHumanReadable(state);
  const problemsText = problems.map((category) => categoryTitles[category]).join(', ');

  const [commentText, setCommentText] = useState(LOADING_COMMENT_TEXT);

  const getCommentText = async () => {
    try {
      const comment = await fetchComment(scanId);
      setCommentText(comment || NO_COMMENT_TEXT);
    } catch (_error) {
      setCommentText(ERROR_COMMENT_TEXT);
    }
  };

  useEffect(() => {
    getCommentText();
  }, [scanId, state, problems]);

  const hasRemark =
    commentText !== NO_COMMENT_TEXT &&
    commentText !== LOADING_COMMENT_TEXT &&
    commentText !== ERROR_COMMENT_TEXT;

  const getIcon = () => {
    if (hasRemark || problems.length > 0) {
      return (
        <CircleInformationFilled
          data-testid="to-rework-icon"
          fill={qaLabelColors[state].color}
          fontSize={16}
        />
      );
    }
    return undefined;
  };

  const tooltipContent = [
    text,
    qaStateDescriptions[state],
    problemsText,
    commentText !== NO_COMMENT_TEXT &&
    commentText !== LOADING_COMMENT_TEXT &&
    commentText !== ERROR_COMMENT_TEXT
      ? commentText
      : null,
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <Tooltip
      arrow
      title={<Typography style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</Typography>}
      placement="top"
    >
      <Chip
        icon={getIcon()}
        size="small"
        sx={{
          maxWidth: 180,
          minWidth: 0,
          backgroundColor: qaLabelColors[state].backgroundColor,
          color: qaLabelColors[state].color,
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          },
        }}
        label={text}
      />
    </Tooltip>
  );
};

export const ScanListItem = ({ scan }: { scan: Scan }) => {
  const { checkedScans, isSubmittingQaState } = useSnapshot(checkboxSelectionState);
  const isChecked = checkedScans.some(({ id }) => id === scan.id);
  const onToggleChecked = useCallback(() => toggleCheckedScans([scan]), [scan]);
  const ref = useRef<HTMLDivElement>(null);
  const isScanSelected = useSelector(viewer3dSelectors.scanIdIsSelected(scan.id));
  useEffect(() => {
    isScanSelected && ref.current?.scrollIntoView({ block: 'center' });
  }, [isScanSelected]);
  const dispatch = useAppDispatch();

  const setSelectedScanId = () => {
    dispatch(viewer3dActions.setSelectedScanId({ id: scan.id, focus: false }));
  };

  const patchScan = useCallback(
    (updatedScan: Partial<Scan>) => {
      dispatch(
        scanThunks.patch({
          path: ['scans', scan.id],
          body: { ...updatedScan, id: scan.id },
        }),
      );
    },
    [scan.id, dispatch],
  );

  const toggleVisibility = () => patchScan({ visible: !scan.visible });

  const { scanGrouping } = useSnapshot(scanGroupingState);
  const scanDate = DateTime.fromISO(
    scanGrouping === 'recordedAt' ? (scan.metadata?.beginDate ?? scan.createdAt) : scan.uploadedAt!,
  );

  return (
    <ListItemButton
      role="menuitem"
      ref={ref}
      sx={{
        opacity: scan.visible ? 1 : 0.5,
        backgroundColor: isChecked ? 'secondary.dark' : 'inherit',
      }}
      key={scan.id}
      dense
      disableGutters
      disableRipple
      divider
      selected={isScanSelected}
      onClick={(e) => e.stopPropagation()}
    >
      <ListItemIcon>
        <Checkbox
          checked={isChecked}
          onChange={onToggleChecked}
          disabled={scan.qaState === 'SCAN_ONLY_DONE' || isSubmittingQaState}
        />
      </ListItemIcon>
      <ListItemText
        onClick={setSelectedScanId}
        tabIndex={0}
        primary={
          <Stack direction="row" gap={1} alignItems="center">
            <Typography variant="subtitle2">
              {scanDate.isValid ? scanDate.toLocaleString(DateTime.TIME_24_SIMPLE) : 'Unknown Time'}
            </Typography>
            <ProcessingIndicator
              uploadedAt={scan.uploadedAt}
              size={16}
              processingResultId={scan.selectedProcessingResultId}
            />
            <QAStateIndicator
              state={scan.qaState}
              problems={scan.reworkCategories}
              scanId={scan.id}
            />
          </Stack>
        }
        secondary={
          <Typography variant="body2" color="text.secondary" noWrap title={scan.location?.place}>
            {scan.location?.place}
          </Typography>
        }
      />
      <IconButton onClick={toggleVisibility} color="secondary">
        {scan.visible ? <EyeOutline /> : <EyeSlashedOutline />}
      </IconButton>
    </ListItemButton>
  );
};
